// Atoms / PartyBuddy Scout output contract + JSON paste parser (Option 1 MVP).
//
// This is a CLIENT-SIDE, no-backend, no-AI, no-scraping helper. It lets an admin
// paste structured results copied out of the Atoms assistant (partybuddy.atoms.world)
// OR the Perplexity "PartyBuddy Event Scout" prompt into the Event Assistant or
// News/Reviews AI Assistant. Both sources emit the same JSON contract, so one
// parser serves both.
//
// EXPECTED OUTPUT FORMAT — a JSON object OR an array of objects:
//
//   [
//     {
//       "name": "",          // event name (events use this)
//       "title": "",         // post title (news/reviews use this); name/title interchangeable
//       "city": "",
//       "country": "",
//       "venue": "",
//       "date": "YYYY-MM-DD",
//       "genres": [],        // array of strings, or a comma-separated string
//       "moods": [],         // array of strings, or a comma-separated string
//       "description": "",
//       "sourceUrl": "",
//       "imageUrl": "",
//       "score": 0,
//       "scoreReason": "",
//       "priorityRegion": "",
//       "confidence": 0,
//       "notes": ""
//     }
//   ]
//
// Behavior:
// - Accepts a JSON array (→ one suggestion per item) or a single JSON object.
// - Strips ```json ... ``` markdown fences.
// - If the paste has prose/citations before or after the JSON (common with
//   Perplexity, e.g. "Here are 5 events [1][2]:\n\n[ ... ]\n\nSources: ..."),
//   safely locates the real JSON array using a string/escape-aware balanced
//   bracket scan — never a greedy "first [ to last ]" regex, which would
//   swallow or get confused by unrelated brackets like citation markers.
// - Returns an array of normalized, deduplicated suggestions when valid JSON
//   with at least one usable row is found.
// - Returns null when the text is empty / not valid JSON / has no usable rows —
//   the caller then falls back to its existing local/mock manual behavior.
// - Never throws, never uses eval()/Function(); invalid rows are skipped
//   independently, missing fields default safely (never invented).

const asString = (value) => {
  if (typeof value === "string") return value.trim();
  return "";
};

const asStringArray = (value) => {
  if (Array.isArray(value)) {
    return value
      .filter((item) => typeof item === "string" && item.trim())
      .map((item) => item.trim());
  }
  if (typeof value === "string" && value.trim()) {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

// score/confidence must always become a finite number; missing/invalid -> 0.
// Never invents a "plausible-looking" value — 0 simply means "unknown".
const asNumber = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value.trim());
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
};

// Atoms/Scout sometimes emit smart/curly quotes instead of valid JSON quotes.
// Normalize them so JSON.parse succeeds.
const normalizeQuotes = (raw) => raw.replace(/[“”]/g, '"').replace(/[‘’]/g, "'");

// Remove a surrounding ```json ... ``` (or plain ``` ... ```) markdown fence.
const stripCodeFences = (raw) => {
  const fenced = raw.match(/```(?:json)?\s*([\s\S]*?)```/i);
  return (fenced ? fenced[1] : raw).trim();
};

// Scan `raw` for every *top-level* balanced [...] / {...} substring, respecting
// JSON string literals (so brackets inside quoted strings — e.g. a description
// containing "[VIP Area]" — never affect bracket depth) and escaped quotes
// (\" doesn't end a string). This deliberately does NOT track single quotes as
// string delimiters (valid JSON only ever uses double quotes), so apostrophes
// in ordinary prose like "buddy's" can't accidentally swallow real brackets.
//
// Returns candidates in the order they appear in the text. A citation marker
// like "[1]" is itself a balanced (if semantically useless) candidate — that's
// fine, callers try every candidate and keep whichever parses into the most
// valid rows, rather than assuming the first/longest bracket pair is correct.
const scanTopLevelJsonCandidates = (raw) => {
  const candidates = [];
  let depth = 0;
  let start = -1;
  let inString = false;
  let escaped = false;

  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i];

    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (ch === "\\") {
        escaped = true;
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }

    if (ch === '"') {
      inString = true;
      continue;
    }

    if (ch === "[" || ch === "{") {
      if (depth === 0) start = i;
      depth++;
      continue;
    }

    if (ch === "]" || ch === "}") {
      if (depth > 0) {
        depth--;
        if (depth === 0 && start !== -1) {
          candidates.push(raw.slice(start, i + 1));
          start = -1;
        }
      }
    }
  }

  return candidates;
};

const mapRow = (row) => {
  if (!row || typeof row !== "object" || Array.isArray(row)) return null;
  // Support both name and title; either one satisfies the other.
  const name = asString(row.name) || asString(row.title);
  const title = asString(row.title) || asString(row.name);
  if (!name && !title) return null; // need at least one label

  return {
    name,
    title,
    city: asString(row.city),
    country: asString(row.country),
    venue: asString(row.venue),
    date: asString(row.date),
    genres: asStringArray(row.genres),
    moods: asStringArray(row.moods),
    description: asString(row.description),
    sourceUrl: asString(row.sourceUrl),
    imageUrl: asString(row.imageUrl),
    score: asNumber(row.score),
    scoreReason: asString(row.scoreReason),
    priorityRegion: asString(row.priorityRegion),
    confidence: asNumber(row.confidence),
    notes: asString(row.notes) || "Imported from Atoms/Scout — review before approving.",
  };
};

// Object keys that may wrap the actual event array.
const ARRAY_KEYS = ["events", "items", "results", "data", "suggestions"];

// Resolve parsed JSON into an array of row objects. Handles:
// - a raw array (→ one row per element; this is the normal Scout case)
// - a double-encoded JSON string (string that itself contains JSON)
// - an object wrapping an array under a known key (events/items/results/…)
// - that wrapper value also being a JSON string
// - a single object (→ one row)
const coerceToRows = (value, depth = 0) => {
  if (depth > 5 || value == null) return [];

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      return coerceToRows(JSON.parse(trimmed), depth + 1);
    } catch {
      return [];
    }
  }

  if (Array.isArray(value)) return value;

  if (typeof value === "object") {
    for (const key of ARRAY_KEYS) {
      if (key in value) {
        const rows = coerceToRows(value[key], depth + 1);
        if (rows.length) return rows;
      }
    }
    return [value]; // a single event object
  }

  return [];
};

// Turn one JSON.parse()-able candidate string into normalized suggestion rows.
// Each row is validated/normalized independently (via mapRow), so a single
// malformed object inside an otherwise-valid array never discards the rest.
const rowsFromJsonCandidate = (candidate) => {
  try {
    const data = JSON.parse(candidate);
    return coerceToRows(data).map(mapRow).filter(Boolean);
  } catch {
    return [];
  }
};

// De-duplicate suggestions from the same paste. Prefers sourceUrl identity
// (when non-empty) over the combination of normalized name + date + city +
// venue, so two genuinely different events in the same city/venue are never
// accidentally merged just because they share a location.
const dedupeSuggestions = (rows) => {
  const seen = new Set();
  const result = [];
  for (const row of rows) {
    const normalizedUrl = row.sourceUrl.trim().toLowerCase();
    const key = normalizedUrl
      ? `url:${normalizedUrl}`
      : `id:${(row.name || row.title).trim().toLowerCase()}|${row.date}|${row.city.toLowerCase()}|${row.venue.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(row);
  }
  return result;
};

export const parseAtomsSuggestions = (text) => {
  if (!text || !text.trim()) return null;

  const unfenced = normalizeQuotes(stripCodeFences(text));

  // Fast path: the whole (unfenced) paste is already valid JSON — the normal
  // case for a clean Scout array, with or without surrounding whitespace.
  const direct = rowsFromJsonCandidate(unfenced);

  // Fallback: prose/citations around the JSON prevented a direct parse (or
  // the direct parse "succeeded" but yielded zero usable rows, e.g. the text
  // parsed as a bare string/number). Safely extract every top-level bracketed
  // substring and keep whichever candidate yields the most valid rows.
  let best = direct;
  if (best.length === 0) {
    const candidates = scanTopLevelJsonCandidates(unfenced);
    for (const candidate of candidates) {
      const rows = rowsFromJsonCandidate(candidate);
      if (rows.length > best.length) best = rows;
    }
  }

  const suggestions = dedupeSuggestions(best);

  if (process.env.NODE_ENV !== "production") {
    console.log("Parsed Atoms/Scout suggestions:", suggestions.length);
  }

  return suggestions.length ? suggestions : null;
};
